/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2025 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * CourseInformationStyles
 *
 * @file CourseInformationStyles.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import { styled } from '@mui/material/styles';
import { Button, IconButton, Typography } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';

export const Section = styled('div')({
  '&&': {
    background: mixins.chalkWhiteFont,
    borderRadius: 12,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderWidth: '1px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0,
    boxSizing: 'border-box',
    '& *, &': {
      boxSizing: 'border-box'
    }
  }
});

export const Inner = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  }
});

export const Course = styled('div')({
  '&&': {
    background: mixins.quaternary,
    borderRadius: '8px 8px 0px 0px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderWidth: '1px',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  }
});

export const TitleWrapper = styled('h2')({
  '&&': {
    padding: '8px 24px 9px 8px',
    margin: 0,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '33.18px',
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    fontWeight: mixins.fontWeight500
  }
});

export const MetaInfoContainer = styled('div')({
  '&&': {
    padding: '8px',
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%'
  }
});

export const Metadetails = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  }
});

export const Label = styled('div')({
  '&&': {
    color: mixins.zambezi,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: '500',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
});

export const Metavalue = styled('div')({
  '&&': {
    color: mixins.color_text_primary,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    lineHeight: mixins.lineHeight24,
    fontWeight: mixins.fontWeight600,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
});

export const Divider = styled('div')({
  '&&': {
    background: 'rgba(13, 0, 77, 0.15)',
    alignSelf: 'stretch',
    flexShrink: 0,
    width: 1
  }
});

export const Book = styled('div')({
  '&&': {
    borderRadius: '12px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderWidth: '1px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  }
});

export const Figure = styled('div')({
  '&&': {
    borderRadius: '8px',
    flexShrink: 0,
    width: 78,
    height: 104,
    overflow: 'hidden',
    aspectRatio: '78/104',
    position: 'relative'
  }
});

export const Img = styled('img')({
  '&&': {
    width: 78,
    height: '100%',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    top: 0,
    objectFit: 'cover',
    aspectRatio: '78/104'
  }
});

export const Meta = styled('div')({
  '&&': {
    padding: '12px 0 12px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    flex: 1
  }
});

export const BookInfo = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  }
});

export const BookTitle = styled('div')({
  '&&': {
    color: mixins.color_text_primary,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '19.2px',
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    fontWeight: mixins.fontWeight500,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
});

export const Authors = styled('div')({
  '&&': {
    color: mixins.zambezi,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '160%',
    fontWeight: '400',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
});

export const ButtonSecondary = styled(Button)({
  '&&': {
    borderRadius: '1000px',
    borderStyle: 'solid',
    borderColor: mixins.navyColor,
    borderWidth: '2px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'row',
    gap: 0,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  }
});

export const OpenBookButton = styled(Button)({
  '&&': {
    borderRadius: '1000px',
    border: 'none',
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 0,
    paddingBottom: 2,
    display: 'flex',
    flexDirection: 'row',
    gap: 0,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 'unset',
    background: 'transparent',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  }
});

export const TextContainer = styled('span')({
  '&&': {
    padding: '0 8px 2px 8px',
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  }
});

export const Label2 = styled('span')({
  '&&': {
    color: mixins.navyColor,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '100%',
    fontWeight: mixins.fontWeight500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
});

export const InstructorContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
});

export const Avatar = styled('img')({
  '&&': {
    display: 'flex',
    width: '32px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%'
  }
});

export const InstructorName = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '22px'
  }
});

export const ActionButtonsContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto'
  }
});

export const IconButtonStyled = styled(IconButton)({
  '&&': {
    display: 'flex',
    width: '32px',
    height: '32px',
    padding: '12px',
    justifyContent: 'center',
    alignItems: 'center',

    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18
    }
  }
});

export const TertiaryButton = styled(Button)({
  '&&': {
    display: 'flex',
    padding: '8px 16px',
    justifyContent: 'center',
    alignItems: 'center',
    color: mixins.navyColor,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '22px',
    borderRadius: '1000px',
    minWidth: 'unset',
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  }
});
